/**
 * Control Inheritance — Pure Resolver
 *
 * No React. No side-effects beyond the injected fetchSchema function.
 *
 * resolveOverride — fetches one base control, deep-merges derived on top.
 *                   Inheritance markers are excluded before merging so they
 *                   never appear in the resolved result.
 * resolveImport  — fetches one or many base controls as-is (no merge).
 *                   Returns deep copies so the module-level cache is never
 *                   mutated by anything downstream.
 */

import type { FormControl, FormSchema } from '@atlas-forms/types-js';
import { isInheritedControl } from './types';
import { deepMerge } from './merge';

// ─── Override (1-to-1) ────────────────────────────────────────────────────────

export async function resolveOverride(
  localDef: FormControl,
  fetchSchema: (formId: number) => Promise<FormSchema>,
): Promise<FormControl> {
  if (!isInheritedControl(localDef)) return localDef;

  // Single destructure — gets the values we need AND produces cleanLocalDef
  // without the inheritance markers, so they never enter the merged result.
  const {
    referredFormID,
    referredControlID,
    controlInheritType: _ct,
    ...cleanLocalDef
  } = localDef as Record<string, unknown>;

  if (String(referredControlID).includes(',') || String(referredControlID).trim() === '*') {
    console.warn(
      `[control-inheritance] Override mode only supports a single control ID. ` +
      `"${referredControlID}" looks like a multi-value — did you mean controlInheritType: "import"?`,
    );
  }

  const baseSchema = await fetchSchema(referredFormID as number);
  const baseControl = (baseSchema.controls ?? []).find(
    (c) => c.id === String(referredControlID).trim(),
  );

  if (!baseControl) {
    throw new Error(
      `Control "${referredControlID}" not found in form ${referredFormID}`,
    );
  }

  return deepMerge(baseControl, cleanLocalDef) as FormControl;
}

// ─── Import (1-to-N) ──────────────────────────────────────────────────────────

export async function resolveImport(
  localDef: FormControl,
  fetchSchema: (formId: number) => Promise<FormSchema>,
): Promise<FormControl[]> {
  if (!isInheritedControl(localDef)) return [localDef];

  const { referredFormID, referredControlID } = localDef;

  const baseSchema = await fetchSchema(referredFormID);
  const allControls = baseSchema.controls ?? [];

  let selected: FormControl[];

  if (referredControlID.trim() === '*') {
    selected = allControls;
  } else {
    const ids = referredControlID.split(',').map((s) => s.trim()).filter(Boolean);
    selected = ids.map((id) => {
      const ctrl = allControls.find((c) => c.id === id);
      if (!ctrl) throw new Error(`Control "${id}" not found in form ${referredFormID}`);
      return ctrl;
    });
  }

  // Deep-copy each control so the cache is never mutated downstream.
  // structuredClone is available in all modern browsers and Node 17+.
  const copied = selected.map((ctrl) => structuredClone(ctrl) as FormControl);

  // Warn if any imported control is itself inherited — chained inheritance is not
  // resolved recursively; the markers will remain in the final rendered schema.
  for (const ctrl of copied) {
    if (isInheritedControl(ctrl)) {
      console.warn(
        `[control-inheritance] Imported control "${ctrl.id}" from form ${referredFormID} is itself ` +
        `inherited (referredFormID: ${ctrl.referredFormID}). ` +
        `Chained inheritance is not resolved — its markers will remain in the rendered form.`,
      );
    }
  }

  return copied;
}
